import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { ChevronDown, ChevronRight, Loader2, RotateCcw, TrendingUp } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { useConfiguredDashboard } from '@/hooks/useAnalytics';
import { useCampaigns } from '@/hooks/useCampaigns';
import { usePageTitle } from '@/hooks/usePageTitle';
import { type GlobalFilters } from '@/lib/api/analytics.api';
import { cn } from '@/lib/utils';
import { useChatStore } from '@/stores/chat.store';

import { CallsPanel } from './dashboard/components/CallsPanel';
import { MindMapCanvas } from './dashboard/components/MindMapCanvas';
import { ACCENT } from './dashboard/components/MindMapNode';

import type { DashboardConfig, Direction, TreeNodeMetric } from '@repo/shared-types';

const DIRECTIONS: { value: Direction; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'INBOUND', label: 'Inbound' },
  { value: 'OUTBOUND', label: 'Outbound' },
];

const TRANSPORTS = ['ALL', 'PSTN', 'WEBRTC', 'WHATSAPP', 'WEB_WIDGET', 'EMAIL'] as const;
type Transport = (typeof TRANSPORTS)[number];

// ─── Config-driven Mind Map ──────────────────────────────────────────────────
function ConfiguredMindMap({
  config,
  data,
  direction,
  selectedClassification,
  onClassificationSelect,
  onDirectionSelect,
  disableSplit,
  onReactFlowReady,
}: {
  config: DashboardConfig;
  data: Record<string, TreeNodeMetric>;
  direction: Direction;
  selectedClassification: string | null;
  onClassificationSelect: (classification: string | null, metricKey?: string | null) => void;
  onDirectionSelect: (dir: Direction | null) => void;
  disableSplit?: boolean;
  onReactFlowReady?: (instance: { fitViewToNode: (metricKey: string) => void }) => void;
}) {
  return (
    <MindMapCanvas
      config={config}
      data={data}
      direction={direction}
      selectedClassification={selectedClassification}
      onClassificationSelect={onClassificationSelect}
      onDirectionSelect={onDirectionSelect}
      disableSplit={disableSplit}
      onReactFlowReady={onReactFlowReady}
    />
  );
}

// ─── Mobile stat card ────────────────────────────────────────────────────────
function MobileStatCard({
  label,
  count,
  pct,
  accent,
  classification,
  metricKey,
  onSelect,
  selected,
}: {
  label: string;
  count: number;
  pct?: string;
  accent: string;
  classification: string;
  metricKey?: string;
  onSelect: (c: string, metricKey?: string) => void;
  selected: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(classification, metricKey)}
      className={cn(
        'flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors',
        selected ? 'border-primary/30 bg-muted' : 'border-border hover:bg-muted/50',
      )}
    >
      <div className="flex items-center gap-2.5">
        <div className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: accent }} />
        <span className="text-foreground text-sm font-medium">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        {pct && <span className="text-muted-foreground text-xs">{pct}</span>}
        <span className="text-sm font-bold tabular-nums" style={{ color: accent }}>
          {count.toLocaleString()}
        </span>
        <ChevronRight className="text-muted-foreground/50 h-3.5 w-3.5" />
      </div>
    </button>
  );
}

// ─── Collapsible section ─────────────────────────────────────────────────────
function MobileSection({
  title,
  accent,
  defaultOpen = false,
  children,
}: {
  title: string;
  accent: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="overflow-hidden rounded-2xl border">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="bg-muted/50 flex w-full items-center justify-between px-4 py-3"
      >
        <span className="text-xs font-bold tracking-widest uppercase" style={{ color: accent }}>
          {title}
        </span>
        <ChevronDown
          className={cn('text-muted-foreground h-4 w-4 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && <div className="flex flex-col gap-1.5 p-2">{children}</div>}
    </div>
  );
}

// ─── Config-driven mobile view ───────────────────────────────────────────────
function ConfiguredMobileView({
  config,
  data,
  selected,
  onSelect,
}: {
  config: DashboardConfig;
  data: Record<string, TreeNodeMetric>;
  selected: string | null;
  onSelect: (c: string, metricKey?: string) => void;
}) {
  const pctStr = (part: number, whole: number) =>
    whole > 0 ? `${Math.round((part / whole) * 100)}%` : undefined;

  // Group nodes by parent edges to create sections
  const rootNodes = config.tree.nodes.filter((n) => !n.parentMetricKey);
  const childMap = new Map<string, typeof config.tree.nodes>();
  for (const edge of config.tree.edges) {
    const children = childMap.get(edge.source) ?? [];
    const child = config.tree.nodes.find((n) => n.id === edge.target);
    if (child) children.push(child);
    childMap.set(edge.source, children);
  }

  function renderNodeAndChildren(nodeId: string, depth = 0): React.ReactNode {
    const nodeCfg = config.tree.nodes.find((n) => n.id === nodeId);
    if (!nodeCfg) return null;

    // Fall back to metricKeys[0] when the editor stored the value as a
    // single-element metricKeys array (cleared metricKey). Otherwise the
    // mobile card renders 0 for an otherwise-valid metric.
    const effectiveMetricKey = nodeCfg.metricKey ?? nodeCfg.metricKeys?.[0];
    const metric = effectiveMetricKey ? (data[effectiveMetricKey] ?? { count: 0 }) : { count: 0 };
    const parentMetric = nodeCfg.parentMetricKey ? data[nodeCfg.parentMetricKey] : null;
    const accent = ACCENT[nodeCfg.colorClass] ?? '#64748b';
    const children = childMap.get(nodeId) ?? [];

    return (
      <div key={nodeId}>
        <MobileStatCard
          label={nodeCfg.label}
          count={metric.count}
          pct={parentMetric ? pctStr(metric.count, parentMetric.count) : undefined}
          accent={accent}
          classification={nodeCfg.classification}
          metricKey={effectiveMetricKey}
          onSelect={onSelect}
          selected={selected === nodeCfg.classification}
        />
        {/* Render sub-items */}
        {nodeCfg.subItems?.map((sub) => {
          const sm = data[sub.metricKey] ?? { count: 0 };
          const base = data[sub.pctBaseMetricKey] ?? { count: 0 };
          const subAccent = ACCENT[sub.colorClass] ?? '#64748b';
          return (
            <MobileStatCard
              key={sub.metricKey}
              label={sub.label}
              count={sm.count}
              pct={pctStr(sm.count, base.count)}
              accent={subAccent}
              classification={sub.classification}
              metricKey={sub.metricKey}
              onSelect={onSelect}
              selected={selected === sub.classification}
            />
          );
        })}
        {children.map((child) => renderNodeAndChildren(child.id, depth + 1))}
      </div>
    );
  }

  // Build sections from root nodes and their immediate children
  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-4">
      {rootNodes.map((root) => {
        const accent = ACCENT[root.colorClass] ?? '#64748b';
        return (
          <MobileSection key={root.id} title={root.label} accent={accent} defaultOpen>
            {renderNodeAndChildren(root.id)}
          </MobileSection>
        );
      })}
    </div>
  );
}

// ─── Summary widget ──────────────────────────────────────────────────────────
function SummaryWidget({
  data,
  config,
}: {
  data: Record<string, TreeNodeMetric>;
  config: DashboardConfig;
}) {
  // Helper: same single-element metricKeys fallback the rest of the page uses.
  const keyOf = (n?: { metricKey?: string; metricKeys?: string[] }) =>
    n?.metricKey ?? n?.metricKeys?.[0];

  const rootNode = config.tree.nodes.find((n) => !n.parentMetricKey);
  const rootKey = keyOf(rootNode);
  const totalMetric = rootKey ? data[rootKey] : null;
  const total = totalMetric?.count ?? 0;

  const answeredNode = config.tree.nodes.find((n) => n.id === 'answered');
  const notAnsweredNode = config.tree.nodes.find((n) => n.id === 'not_answered');
  const engagedNode = config.tree.nodes.find((n) => n.id === 'engaged');

  const answeredKey = keyOf(answeredNode);
  const notAnsweredKey = keyOf(notAnsweredNode);
  const answered = answeredKey ? (data[answeredKey]?.count ?? 0) : null;
  const notAnswered = notAnsweredKey ? (data[notAnsweredKey]?.count ?? 0) : null;
  const engagedKey = keyOf(engagedNode);
  const engaged = engagedKey ? (data[engagedKey]?.count ?? 0) : null;

  const pct = (part: number, whole: number) =>
    whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—';

  return (
    <div className="bg-background/95 pointer-events-none absolute bottom-5 left-5 z-10 w-60 rounded-2xl border p-4 shadow-2xl backdrop-blur-md select-none">
      <div className="mb-3 flex items-center gap-2">
        <TrendingUp className="h-3.5 w-3.5 text-blue-400" />
        <span className="text-muted-foreground text-[11px] font-bold tracking-widest uppercase">
          Overview
        </span>
      </div>
      <div className="space-y-2">
        <StatRow label="Total" value={total.toLocaleString()} accent="var(--foreground)" />
        {answered !== null && (
          <StatRow
            label="Answered"
            value={`${answered.toLocaleString()} (${pct(answered, total)})`}
            accent="#10b981"
          />
        )}
        {notAnswered !== null && (
          <StatRow
            label="Not answered"
            value={`${notAnswered.toLocaleString()} (${pct(notAnswered, total)})`}
            accent="#f43f5e"
          />
        )}
        {engaged !== null && answered !== null && (
          <>
            <div className="bg-border my-2 h-px" />
            <StatRow label="Engagement rate" value={pct(engaged, answered)} accent="#14b8a6" />
          </>
        )}
      </div>
    </div>
  );
}

function Divider() {
  return <div className="bg-border h-4 w-px shrink-0" />;
}

function StatRow({ label, value, accent }: { label: string; value: string; accent: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground text-[11px]">{label}</span>
      <span className="text-[13px] font-bold tabular-nums" style={{ color: accent }}>
        {value}
      </span>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function findLabelForClassification(
  config: DashboardConfig,
  classification: string,
): string | undefined {
  for (const node of config.tree.nodes) {
    if (node.classification === classification) return node.label;
    for (const sub of node.subItems ?? []) {
      if (sub.classification === classification) return sub.label;
    }
  }
  return undefined;
}

/**
 * For complementary nodes: returns the parent classification + sibling excludes.
 * The CallsPanel should use parentClassification instead of the node's own classification,
 * and exclude all sibling classifications to get the "everything else" set.
 *
 * Also returns the corresponding metric keys (v2 drilldown) so the panel can
 * send those to the backend when ANALYTICS_V2_ENABLED is on.
 */
function getComplementaryOverride(
  config: DashboardConfig,
  selectedClassification: string | null,
):
  | {
      classification: string;
      excludes: string[];
      metricKey?: string;
      excludeMetricKeys: string[];
    }
  | undefined {
  if (!selectedClassification) return undefined;

  const selectedNode = config.tree.nodes.find(
    (n) => n.classification === selectedClassification && n.complementary,
  );
  if (!selectedNode) return undefined;

  // Find parent via edges
  const parentEdge = config.tree.edges.find((e) => e.target === selectedNode.id);
  if (!parentEdge) return undefined;

  const parentNode = config.tree.nodes.find((n) => n.id === parentEdge.source);
  if (!parentNode?.classification) return undefined;

  // Find all sibling node IDs (children of same parent, excluding self)
  const siblingIds = config.tree.edges
    .filter((e) => e.source === parentEdge.source && e.target !== selectedNode.id)
    .map((e) => e.target);

  // Collect classifications + metric keys from non-complementary siblings.
  // Falls back to metricKeys[0] for nodes saved via the "Add metric" UI which
  // stores single values in the array form and leaves metricKey undefined.
  const excludes: string[] = [];
  const excludeMetricKeys: string[] = [];
  for (const sibId of siblingIds) {
    const sibNode = config.tree.nodes.find((n) => n.id === sibId);
    if (!sibNode || sibNode.complementary) continue;
    if (sibNode.classification) excludes.push(sibNode.classification);
    const sibKey = sibNode.metricKey ?? sibNode.metricKeys?.[0];
    if (sibKey) excludeMetricKeys.push(sibKey);
  }

  const hasExclusions = excludes.length > 0 || excludeMetricKeys.length > 0;
  return hasExclusions
    ? {
        classification: parentNode.classification,
        excludes,
        metricKey: parentNode.metricKey ?? parentNode.metricKeys?.[0],
        excludeMetricKeys,
      }
    : undefined;
}

// ─── Main page ───────────────────────────────────────────────────────────────
export const ConfiguredDashboardPage = () => {
  usePageTitle('Analytics');
  // All filters live in the URL (?mode=contacts&from=...&to=...&direction=...
  // &campaign=...&transport=...&panel=...&metric=...&pdir=...) so any dashboard
  // view is shareable, bookmarkable, and survives refresh. Defaults stay out
  // of the URL; `replace` avoids polluting browser history while typing.
  const [searchParams, setSearchParams] = useSearchParams();

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      // Base on window.location.search, NOT the hook's snapshot: a node click
      // triggers two updates in the same tick (panel/metric, then pdir), and
      // snapshot-based updaters would each start from the pre-click URL —
      // the second wiping the first. history.replace commits synchronously,
      // so reading the live URL keeps them cumulative.
      setSearchParams(
        () => {
          const next = new URLSearchParams(window.location.search);
          for (const [key, value] of Object.entries(updates)) {
            if (value) next.set(key, value);
            else next.delete(key);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // 'contacts' counts distinct contacts per classification per day instead of calls.
  const mode: 'calls' | 'contacts' = searchParams.get('mode') === 'contacts' ? 'contacts' : 'calls';
  const dirParam = searchParams.get('direction');
  const direction: Direction = dirParam === 'INBOUND' || dirParam === 'OUTBOUND' ? dirParam : 'ALL';
  const executedAtFrom = searchParams.get('from') ?? '';
  const executedAtTo = searchParams.get('to') ?? '';
  const campaignId = searchParams.get('campaign') ?? '';
  const transportParam = searchParams.get('transport') as Transport | null;
  const callTransport: Transport =
    transportParam && TRANSPORTS.includes(transportParam) ? transportParam : 'PSTN';
  const selectedClassification = searchParams.get('panel');
  const selectedMetricKey = searchParams.get('metric');
  const pdirParam = searchParams.get('pdir');
  const panelDirection: Direction | null =
    pdirParam === 'INBOUND' || pdirParam === 'OUTBOUND' || pdirParam === 'ALL' ? pdirParam : null;

  const setMode = (m: 'calls' | 'contacts') => updateParams({ mode: m === 'contacts' ? m : null });
  const setDirection = (d: Direction) => updateParams({ direction: d === 'ALL' ? null : d });
  const setExecutedAtFrom = (v: string) => updateParams({ from: v || null });
  const setExecutedAtTo = (v: string) => updateParams({ to: v || null });
  const setCampaignId = (v: string) => updateParams({ campaign: v || null });
  const setPanelDirection = (d: Direction | null) => updateParams({ pdir: d ?? null });

  // v2 path: the panel needs both classification (legacy fallback) AND the
  // explicit metricKey from the clicked node so it can resolve the right
  // metric_definitions predicate — by-classification lookup is wrong when
  // multiple nodes share `classification` (e.g. several with `all`).
  const handleSelect = useCallback(
    (classification: string | null, metricKey?: string | null) => {
      // Some v2 nodes carry an empty classification and drive the drill-down
      // purely off metricKey. An empty string would be dropped from the URL
      // (closing the panel), so store 'all' — the API treats '' and 'all'
      // identically.
      const panel = classification === null ? null : classification || 'all';
      updateParams({ panel, metric: metricKey ?? null });
    },
    [updateParams],
  );

  const mindMapRef = useRef<{ fitViewToNode: (metricKey: string) => void } | null>(null);

  const focusMetric = useCallback((metricKey: string) => {
    mindMapRef.current?.fitViewToNode(metricKey);
  }, []);

  const { data: campaignsData } = useCampaigns({ limit: 100 });
  const campaigns = campaignsData?.data ?? [];

  const globalFilters: GlobalFilters = useMemo(
    () => ({
      direction,
      ...(executedAtFrom ? { executedAtFrom } : {}),
      ...(executedAtTo ? { executedAtTo } : {}),
      ...(campaignId ? { campaignId } : {}),
      ...(callTransport !== 'ALL' ? { callTransport } : {}),
      timePreset: 'all',
      mode,
    }),
    [direction, executedAtFrom, executedAtTo, campaignId, callTransport, mode],
  );

  const { data: response, isLoading, error } = useConfiguredDashboard(globalFilters);

  const setDashboardContext = useChatStore((s) => s.setDashboardContext);

  useEffect(() => {
    if (!response) return;
    const labels: Record<string, string> = {};
    for (const node of response.config.tree.nodes) {
      const key = node.metricKey ?? node.metricKeys?.[0];
      if (key) labels[key] = node.label;
      for (const sub of node.subItems ?? []) {
        if (sub.metricKey) labels[sub.metricKey] = sub.label;
      }
    }
    setDashboardContext(focusMetric, labels);
    return () => setDashboardContext(null, null);
  }, [response, focusMetric, setDashboardContext]);

  if (isLoading && !response) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error && !response) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-destructive text-sm">Failed to load dashboard data.</p>
      </div>
    );
  }

  if (!response) return null;

  const { config, data } = response;

  // Channel calls (web/WebRTC and WhatsApp) don't carry a meaningful inbound/outbound
  // direction or campaign, so they hide those filters and aren't split by direction —
  // WhatsApp behaves exactly like web calls here.
  const isChannelTransport =
    callTransport === 'WEBRTC' ||
    callTransport === 'WHATSAPP' ||
    callTransport === 'WEB_WIDGET' ||
    callTransport === 'EMAIL';

  return (
    <ReactFlowProvider>
      <div className="relative -m-8 flex h-screen flex-col overflow-hidden">
        {/* ── Toolbar ─────────────────────────────────────────────────── */}
        <div className="bg-background/80 flex shrink-0 flex-wrap items-center gap-2 border-b px-4 py-2 backdrop-blur-md">
          <span className="text-foreground text-sm font-semibold tracking-tight">Dashboard</span>

          <Divider />

          {/* Calls vs Contacts counting mode */}
          <div className="flex items-center gap-1 rounded-lg border p-0.75">
            {(['calls', 'contacts'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={cn(
                  'rounded-md px-3 py-1 text-xs font-medium transition-all',
                  mode === m
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {m === 'calls' ? 'Calls' : 'Contacts'}
              </button>
            ))}
          </div>

          <Divider />

          {!isChannelTransport && (
            <div className="flex items-center gap-1 rounded-lg border p-0.75">
              {DIRECTIONS.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => setDirection(d.value)}
                  className={cn(
                    'rounded-md px-3 py-1 text-xs font-medium transition-all',
                    direction === d.value
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {d.label}
                </button>
              ))}
            </div>
          )}

          <div className="hidden items-center gap-2 sm:flex">
            <Divider />
            <label className="text-muted-foreground text-xs font-medium">From</label>
            <input
              type="date"
              value={executedAtFrom}
              onChange={(e) => setExecutedAtFrom(e.target.value)}
              className="focus:ring-primary bg-background text-foreground h-7 rounded-md border px-2 text-xs focus:ring-1 focus:outline-none dark:border-slate-700"
            />
            <span className="text-muted-foreground text-xs">→</span>
            <input
              type="date"
              value={executedAtTo}
              onChange={(e) => setExecutedAtTo(e.target.value)}
              className="focus:ring-primary bg-background text-foreground h-7 rounded-md border px-2 text-xs focus:ring-1 focus:outline-none dark:border-slate-700"
            />
          </div>

          {campaigns.length > 0 && !isChannelTransport && (
            <div className="hidden items-center gap-2 sm:flex">
              <Divider />
              <label className="text-muted-foreground text-xs font-medium">Campaign</label>
              <select
                value={campaignId}
                onChange={(e) => setCampaignId(e.target.value)}
                className="focus:ring-primary bg-background text-foreground h-7 max-w-45 rounded-md border px-2 text-xs focus:ring-1 focus:outline-none dark:border-slate-700"
              >
                <option value="">All campaigns</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="hidden items-center gap-2 sm:flex">
            <Divider />
            <select
              value={callTransport}
              onChange={(e) => {
                const value = e.target.value as Transport;
                // Channel transports carry no direction/campaign — clear them
                // in the same URL update so the calls don't clobber each other.
                updateParams({
                  transport: value === 'PSTN' ? null : value,
                  ...(value !== 'PSTN' && value !== 'ALL'
                    ? { direction: null, campaign: null }
                    : {}),
                });
              }}
              className="focus:ring-primary bg-background text-foreground h-7 rounded-md border px-2 text-xs focus:ring-1 focus:outline-none dark:border-slate-700"
            >
              <option value="ALL">All Channels</option>
              <option value="PSTN">Phone Calls</option>
              <option value="WEBRTC">Web Calls</option>
              <option value="WHATSAPP">WhatsApp</option>
              <option value="WEB_WIDGET">Web Widget</option>
              <option value="EMAIL">Email</option>
            </select>
          </div>

          <Divider />

          <button
            type="button"
            title="Reset all filters"
            onClick={() =>
              updateParams({
                mode: null,
                direction: null,
                from: null,
                to: null,
                campaign: null,
                transport: 'ALL',
                panel: null,
                metric: null,
                pdir: null,
              })
            }
            className="text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors"
          >
            <RotateCcw className="h-3 w-3" />
            Reset
          </button>
        </div>

        {/* ── Canvas — desktop only ─────────────────────────────────── */}
        <div className="relative hidden min-h-0 flex-1 flex-row overflow-hidden md:flex">
          <div className="relative min-h-0 flex-1">
            <ConfiguredMindMap
              config={config}
              data={data}
              direction={direction}
              selectedClassification={selectedClassification}
              onClassificationSelect={handleSelect}
              onDirectionSelect={setPanelDirection}
              disableSplit={isChannelTransport}
              onReactFlowReady={(instance) => {
                mindMapRef.current = instance;
              }}
            />
            <SummaryWidget data={data} config={config} />
          </div>
        </div>

        {/* ── Mobile list — visible below md ────────────────────────── */}
        <div className="relative min-h-0 flex-1 overflow-hidden md:hidden">
          <ConfiguredMobileView
            config={config}
            data={data}
            selected={selectedClassification}
            onSelect={handleSelect}
          />
        </div>

        {/* ── Drill-down panel ──────────────────────────────────────── */}
        <CallsPanel
          classification={
            (getComplementaryOverride(config, selectedClassification)?.classification ??
              selectedClassification) as any
          }
          excludeClassifications={
            getComplementaryOverride(config, selectedClassification)?.excludes
          }
          metricKey={
            getComplementaryOverride(config, selectedClassification)?.metricKey ??
            selectedMetricKey ??
            undefined
          }
          excludeMetricKeys={
            getComplementaryOverride(config, selectedClassification)?.excludeMetricKeys
          }
          globalFilters={
            panelDirection ? { ...globalFilters, direction: panelDirection } : globalFilters
          }
          onClose={() => updateParams({ panel: null, metric: null, pdir: null })}
          classificationLabel={
            selectedClassification
              ? findLabelForClassification(config, selectedClassification)
              : undefined
          }
        />
      </div>
    </ReactFlowProvider>
  );
};
